import type { v2 } from '@deque/billing-service-client';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScrimmedLoader from '~/common/components/ScrimmedLoader';
import StepContainer from '~/common/components/StepContainer';
import type {
  EnterpriseMembers,
  Enterprises
} from '~/common/utils/billing-client/client-v2';
import type { UserAccessChanges } from '../../../containers/UserManagement';
import AccessStep from './AccessStep/AccessStep';
import styles from './EditAccess.css';
import ReviewStep from './ReviewStep/ReviewStep';

export interface EditAccessProps {
  selectedMembers: EnterpriseMembers.AcceptedMember[];
  licenses: Enterprises.License[];
  products: v2.Product[];
  userAccessChanges?: UserAccessChanges;
  setUserAccessChanges: (userAccessChanges: UserAccessChanges) => void;
  handleBulkEditAccess: () => Promise<void>;
  loadingAction: boolean;
}

const EditAccess = ({
  selectedMembers,
  licenses,
  products,
  userAccessChanges,
  setUserAccessChanges,
  handleBulkEditAccess,
  loadingAction
}: EditAccessProps) => {
  const { t } = useTranslation();
  const loaderRef = useRef<HTMLDivElement>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const handlePreviousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const steps = [
    {
      title: t('Access'),
      heading: t('Access'),
      label: t('Access')
    },
    {
      title: t('Review'),
      heading: t('Review'),
      label: t('Review')
    }
  ];

  useEffect(() => {
    /* istanbul ignore next */
    loaderRef.current?.focus();
  }, [loaderRef.current]);

  return (
    <>
      {loadingAction && (
        <ScrimmedLoader
          label={t('Loading...')}
          loaderRef={loaderRef}
          focusTrap
        />
      )}
      <StepContainer
        title={t('Edit Access')}
        heading={steps[currentStep].heading}
        className={styles.container}
        steps={steps.map((step, index) => ({
          status:
            currentStep === index
              ? 'current'
              : currentStep > index
                ? 'complete'
                : 'future',
          label: step.label
        }))}
      >
        {currentStep === 0 && (
          <AccessStep
            selectedMembers={selectedMembers}
            licenses={licenses}
            products={products}
            handleSubmit={handleNextStep}
            userAccessChanges={
              userAccessChanges || {
                grantGeneralProducts: [],
                grantAdminProducts: [],
                grantViewProducts: [],
                removeProducts: []
              }
            }
            setUserAccessChanges={setUserAccessChanges}
          />
        )}
        {currentStep === 1 && (
          <ReviewStep
            selectedMembers={selectedMembers}
            handlePreviousStep={handlePreviousStep}
            userAccessChanges={
              userAccessChanges || {
                grantGeneralProducts: [],
                grantAdminProducts: [],
                grantViewProducts: [],
                removeProducts: []
              }
            }
            handleSubmit={handleBulkEditAccess}
          />
        )}
      </StepContainer>
    </>
  );
};

export default EditAccess;
